import { useState } from "react";
import { toast } from "sonner";
import { Check, ChevronDown } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import { CURRENCIES, curSymbol } from "@/lib/format";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export default function CurrencyMenu() {
  const { code, available, changeCurrency, ratesDate } = useCurrency();
  const [busy, setBusy] = useState(false);
  if (!code) return null;

  const pick = async (c) => {
    if (c === code) return;
    setBusy(true);
    try {
      await changeCurrency(c);
      toast.success(`Showing amounts in ${CURRENCIES.find((x) => x.code === c)?.name}`);
    } catch {
      toast.error("Couldn't change currency, please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="currency-menu-button" disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] px-2.5 sm:px-3 py-1.5 text-xs font-semibold text-slate-200 transition-colors">
          <span className="text-[#00C896]">{curSymbol(code)}</span>
          <span data-testid="currency-current-code">{code}</span>
          <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" data-testid="currency-menu" className="w-64 bg-[#14181D] border-white/10 text-slate-200 max-h-[80vh] overflow-y-auto">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Show amounts in</DropdownMenuLabel>
        {CURRENCIES.filter((c) => available.includes(c.code)).map((c) => (
          <DropdownMenuItem key={c.code} data-testid={`currency-option-${c.code}`} onSelect={() => pick(c.code)}
            className="flex items-center gap-3 cursor-pointer focus:bg-white/5 focus:text-white">
            <span className="w-9 text-center font-num font-semibold text-[#00C896]">{curSymbol(c.code)}</span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm">{c.name}</span>
              <span className="block text-[11px] text-slate-500">{c.code} · {c.country}</span>
            </span>
            {c.code === code && <Check className="h-4 w-4 text-[#00C896]" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="bg-white/10" />
        <p className="px-2 py-1.5 text-[11px] leading-relaxed text-slate-500" data-testid="currency-menu-note">
          Bank data is in £ GBP and converted at the daily rate{ratesDate ? ` (${new Date(ratesDate).toLocaleDateString("en-GB", { day: "numeric", month: "short" })})` : ""}. Premium is charged in £.
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
